<template>
  <Layout>
    <user-create-modal
        id="user-create-modal"
        :key="`user-create-modal`"
        @created="$refs.table.$refs.table.refresh()"
    ></user-create-modal>
    <user-edit-modal
        id="user-edit-modal"
        ref="user-edit-modal"
        @updated="$refs.table.$refs.table.refresh()"
    ></user-edit-modal>
    <user-show-modal
        id="user-show-modal"
        ref="user-show-modal"
        @edit="handleEdit"
    ></user-show-modal>
    <div v-if="!lodash.isEmpty(filterColumns)">
      <model-filter
          v-model="filter"
          :columns="filterColumns"
          :modelVariable="modelVariable"
          :filterHandler="filterHandler"
      />
    </div>
    <div class="d-flex justify-content-between">
      <div class="title">{{ lodash.startCase(title) }}</div>
      <div v-if="$auth.hasPermissionTo('approval_rules.view')">
        <router-link
            :to="'/approval_templates'"
            class="btn btn-light bg-white"
        >
          <svg  xmlns="http://www.w3.org/2000/svg" width="16" height="16"  
            fill="currentColor" viewBox="0 0 24 24" class="me-1">
            <path d="M4 19h3v2c0 .36.19.69.51.87a1 1 0 0 0 1-.01L13.27 19h6.72c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2M4 5h16v12h-7c-.18 0-.36.05-.51.14L9 19.23V18c0-.55-.45-1-1-1H4z"></path><path d="m15.71 9.71-1.42-1.42-3.29 3.3-1.29-1.3-1.42 1.42 2.71 2.7z"></path>
          </svg> Approval
        </router-link>
      </div>
    </div>
    <div class="row mt-3">
      <model-index
          ref="table"
          class="col-12"
          :loading="loading"
          v-bind="$props"
          :columns="columns"
          :busy.sync="busy"
          :models-paginated="modelsPaginated"
          :page.sync="page"
          :per-page.sync="perPage"
          :from="from"
          :total="totalFiltered"
          :search.sync="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter-params.sync="filterParams"
          @create-click="handleCreate"
          @export-click="handleExport"
          @show-click="handleShow"
          @edit-click="handleEdit"
          @delete-click="handleDelete"
      >
      </model-index>
    </div>
  </Layout>
</template>

<script>
import ModelIndex from "../models/index-v3"
import UserCreateModal from "@/components/modals/user-create.vue"
import UserEditModal from "@/components/modals/user-edit.vue"
import UserShowModal from "@/components/modals/user-show.vue"
import StatusBadge from "@/components/badges/status-badge.vue";

/* eslint-disable vue/no-unused-components */
export default {
  extends: ModelIndex,
  components: {
    UserCreateModal,
    UserEditModal,
    UserShowModal,
    StatusBadge,
  },
  page: {
    title: "JALA Trade Users",
  },
  props: {
    cardTitle: {default() { return this.lodash.upperCase(`${this.$t("List")} ${this.$t('users.plural')}`) }},
    resourceRoute: {default: 'users'},
    modelVariable: {default: 'user'},
    denyCreate: { type: Boolean, default() { return !this.$auth.hasPermissionTo('users.create') } }
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('users.plural')),
      items: [
        {
          text: this.lodash.upperFirst(this.$t('users.plural')),
          href: "/users",
          active: true,
        },
      ],
      sortBy: (this.$route.query.sort && this.$route.query.sort.split('|').shift().split(',').shift()) || 'name',
      sortDesc: (this.$route.query.sort && this.$route.query.sort.split('|').shift().split(',').pop() === 'desc') || false
    }
  },
  computed: {
    columns() {
      return {
        user: [
          { key: 'index', label: 'No', sortable: false, style: { width: '1%' } },
          {
            key: 'name',
            label: this.lodash.startCase(this.$t('users.name')),
            sortable: true,
            style: { minWidth: '180px' },
            component: {
              props: ['item'],
              template: `
                <div class="text-truncate" style="max-width: 180px">
                  <div class="mb-0" style="color: #171717; font-size: 12px">{{ item.name }}</div>
                  <small style="color: #171717; font-size: 10px">{{ item.email }}</small>
                </div>
              `
            } 
          },
          {
            key: 'roles',
            label: this.lodash.startCase(this.$t('users.role')),
            style: { minWidth: '110px', width: '110px' },
            formatter: (value) => {
              return value.map((role) => role.name ? this.lodash.upperFirst(this.$t(`users.roles.${role.name || 'admin'}`)) : '-').join(', ') || '-';
            }
          },
          {
            key: 'position',
            label: this.lodash.startCase(this.$t('users.position')),
            style: { minWidth: '110px', width: '110px' },
            formatter: (value) => value || '-',
          },
          {
            key: 'created_at',
            label: this.lodash.startCase(this.$t('models.created_at')),
            sortable: true,
            style: { minWidth: '110px', width: '110px' },
            formatter: (value) => (
                this.$moment(value).format('DD MMM, YYYY')
            )
          },
          {
            key: 'is_active',
            label: this.lodash.startCase(this.$t('users.is_active')),
            style: { minWidth: '110px', width: '110px' },
            component: {
              props: ['item'],
              template: `
                <status-badge
                  entity="user"
                  type="status"
                  :status="item.is_active ? 'active' : 'inactive'"
                >
                  {{ item.is_active ? this.lodash.startCase($t('users.active')) : this.lodash.startCase($t('users.inactive')) }}
                </status-badge>
              `
            }
          },
          { key: 'action', label: this.$t('Action'), class: 'text-center', style: { minWidth: '110px', width: '110px' } }
        ].map((field) => ({...field, tdClass: 'align-middle'})),
      };
    },
  },
  methods: {
    getModelName(value) {
      return value.user.email || this.getModelKey(value);
    },
    delete(value) {
      return this.$axios.delete(`/users/${value.id}`)
    },
    fetchModels(page = 1) {
      return this.$axios.get('/users', {
        params: {
          page: page,
          per_page: this.perPage,
          search: this.search,
          sort: `${this.sortBy},${this.sortDesc ? 'desc' : 'asc'}`,
          with: 'user,roles',
          // investor_id__in: 3,
          ...this.filterParams
        }
      }).then((response) => {
        const { data: { data, meta } } = response;
        this.models = data.map((datum) => ({
          ...datum,
          ...this.modelPolicy(),
        }))
        this.page = Number(meta.current_page);
        this.perPage = Number(meta.per_page);
        this.from = Number(meta.from);
        this.total = Number(meta.total);

        return response;
      }).catch((error) => this.failedAlert(error));
    },
    modelPolicy() {
      return {
        _denyShow: !this.$auth.hasPermissionTo('users.view'),
        _denyEdit: !this.$auth.hasPermissionTo('users.edit'),
        _denyDelete: !this.$auth.hasPermissionTo('users.delete'),
      }
    },
    handleCreate() {
      this.$bvModal.show('user-create-modal');
    },
    handleShow(value) {
      this.$refs['user-show-modal'].show(this.getModelKey(value));
    },
    handleEdit(value) {
      this.$refs['user-edit-modal'].edit(this.getModelKey(value));
    },
  },
};
</script>
